import { analytics } from '@/features/analytics/events'
import { useMissionsUrl } from '@/features/missions/context/MissionsUrlProvider'
import { useMissionRedirect } from '@/features/missions/hooks/useMissionRedirect'
import { Text } from '@/ui-kit'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { NectarProject } from '../api/models'
import { MicCheckGate, parseMicCheckConfig } from './mic-check'
import { NectarProjectStatusView, type TerminalStatus } from './NectarProjectStatusView'
import { NectarTaskContent } from './NectarTaskContent'
import { PROJECT_FLOW_COMPONENTS } from './project-flows'

type NectarTaskFlowProps = {
  project: NectarProject
}

export const NectarTaskFlow = ({ project }: NectarTaskFlowProps) => {
  const { missionsUrl } = useMissionsUrl()
  const { redirect } = useMissionRedirect()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const enrollmentStatus = project.enrollment?.status
  const isTerminal = enrollmentStatus === 'APPROVED' || enrollmentStatus === 'REJECTED'
  const isInProgress = enrollmentStatus === 'IN_PROGRESS'
  const enrollmentId = project.enrollment?.id
  const needsMicCheck = project.accessGateEnabled && isInProgress && !!enrollmentId

  const FlowComponent = PROJECT_FLOW_COMPONENTS.get(project.type)

  useEffect(() => {
    analytics.missionState(project.id, enrollmentStatus ?? '')
  }, [])

  const content = FlowComponent ? (
    <FlowComponent
      projectId={project.id}
      projectName={project.name}
      language={project.language}
      captchaEnabled={project.captchaEnabled}
      taskReportingEnabled={project.taskReportingEnabled}
      rewardConfig={{ type: project.rewardConfig?.type ?? 'points', amount: project.totalReward ?? 0 }}
      typeConfig={project.typeConfig ?? null}
      progress={project.progress}
      maxTasksPerUser={project.maxTasksPerUser}
      onBack={() => navigate(redirect ?? missionsUrl)}
      onCompleted={() => {
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECT', project.id] })
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECTS'] })
        if (redirect) navigate(redirect)
      }}
    />
  ) : isTerminal ? (
    <>
      <Text type="h4" className="-mb-2">
        {project?.name}
      </Text>
      <NectarProjectStatusView project={project} status={project.enrollment!.status as TerminalStatus} />
    </>
  ) : (
    <>
      <Text type="h4" className="-mb-2">
        {project?.name}
      </Text>
      <NectarTaskContent project={project} />
    </>
  )

  return needsMicCheck && enrollmentId ? (
    <MicCheckGate
      projectId={project.id}
      enrollmentId={enrollmentId}
      screeningLanguage={project.screeningLanguage ?? project.language}
      accessGateKind={project.accessGateKind}
      accessGatePhrase={project.accessGatePhrase}
      config={parseMicCheckConfig(project.typeConfig)}
    >
      {content}
    </MicCheckGate>
  ) : (
    content
  )
}
